export type Operation = 'addition' | 'subtraction' | 'multiplication' | 'division'
export type Difficulty = 'easy' | 'medium' | 'hard'

export interface MathsQuestion {
  id:        number
  operands:  number[]
  operation: Operation
  answer:    number
  /** Four answer choices — one correct, three distractors */
  choices:   number[]
}

export const OPERATION_ICONS: Record<Operation, string> = {
  addition:       '+',
  subtraction:    '−',
  multiplication: '×',
  division:       '÷',
}

export const OPERATION_EMOJIS: Record<Operation, string> = {
  addition:       '➕',
  subtraction:    '➖',
  multiplication: '✖️',
  division:       '➗',
}

export interface DifficultyConfig {
  /** How many questions per round */
  questionCount: number
  /** Range for operands — [min, max] (inclusive) */
  rangeA: [number, number]
  rangeB: [number, number]
  /** [min, max] number of addends for addition questions */
  additionAddendRange: [number, number]
}

export const DIFFICULTY_CONFIG: Record<Difficulty, DifficultyConfig> = {
  easy:   { questionCount: 8,  rangeA: [1, 10],  rangeB: [1, 10],  additionAddendRange: [2, 2] },
  medium: { questionCount: 10, rangeA: [1, 20],  rangeB: [1, 20],  additionAddendRange: [2, 3] },
  hard:   { questionCount: 12, rangeA: [1, 50],  rangeB: [1, 50],  additionAddendRange: [3, 4] },
}

function randInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1))
}

function makeDistractors(correct: number, count: number): number[] {
  const set = new Set<number>([correct])
  while (set.size < count + 1) {
    const delta = randInt(1, Math.max(5, Math.ceil(Math.abs(correct) * 0.3) + 2))
    const sign  = Math.random() < 0.5 ? 1 : -1
    const candidate = correct + sign * delta
    if (candidate !== correct) set.add(candidate)
  }
  const arr = Array.from(set).filter(n => n !== correct).slice(0, count)
  return arr
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function generateQuestion(
  id: number,
  operations: Operation[],
  cfg: DifficultyConfig,
): MathsQuestion {
  const operation = operations[Math.floor(Math.random() * operations.length)]
  const [minA, maxA] = cfg.rangeA
  const [minB, maxB] = cfg.rangeB

  let operands: number[]
  let answer: number

  switch (operation) {
    case 'addition': {
      const [minAddends, maxAddends] = cfg.additionAddendRange
      const addendCount = randInt(minAddends, maxAddends)
      if (addendCount > 2) {
        // Multi-addend: cap each term at 1–20 so the sum stays manageable
        operands = Array.from({ length: addendCount }, () => randInt(1, 20))
      } else {
        operands = [randInt(minA, maxA), randInt(minB, maxB)]
      }
      answer = operands.reduce((s, n) => s + n, 0)
      break
    }
    case 'subtraction': {
      const a = randInt(minA, maxA)
      const b = randInt(minB, Math.min(a, maxB))   // b ≤ a → no negative answers
      operands = [a, b]
      answer = a - b
      break
    }
    case 'multiplication': {
      // Keep operands small so numbers don't get huge
      const mMax = Math.min(maxA, 12)
      const a = randInt(minA, mMax)
      const b = randInt(minB, mMax)
      operands = [a, b]
      answer = a * b
      break
    }
    case 'division': {
      // Generate b first, then a = b × k (exact division)
      const b = randInt(Math.max(1, minB), Math.min(maxB, 12))
      const maxK = Math.floor(maxA / b)
      const k    = randInt(1, Math.max(1, maxK))
      operands = [b * k, b]
      answer = k
      break
    }
  }

  const distractors = makeDistractors(answer, 3)
  const choices = shuffle([answer, ...distractors])

  return { id, operands, operation, answer, choices }
}

export function generateQuestions(
  operations: Operation[],
  difficulty: Difficulty,
): MathsQuestion[] {
  const cfg = DIFFICULTY_CONFIG[difficulty]
  return Array.from({ length: cfg.questionCount }, (_, i) =>
    generateQuestion(i, operations, cfg)
  )
}
